import { Radio, Square } from 'lucide-react';
import { useState } from 'react';
import { session } from '../lib/session';
import { useStore } from '../store';
import { Drawer } from './ui';

/** Program-feed control: publish a line input into IFB (or any) channels as the "program" source. */
export function ProgramDrawer({ onClose }: { onClose: () => void }) {
  const channels = useStore((s) => s.channels);
  const feeding = useStore((s) => s.feeding);
  const media = useStore((s) => s.media);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const rank = { program: 0, ifb: 1, partyline: 2 } as const;
  const sorted = [...channels].sort((a, b) => rank[a.kind] - rank[b.kind] || a.order - b.order);

  const toggle = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      if (feeding.includes(id)) await session.stopProgram(id);
      else await session.startProgram(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Drawer title="Program feed" onClose={onClose}>
      <div className="drawer__body">
        <p className="muted">
          Publishes the program input (Settings → Program feed input) into a channel. Feed a <strong>Program</strong> channel: everyone listening to it, and every IFB
          linked to it, hears it. IFB listeners get it dimmed whenever someone talks on their IFB.
        </p>
        {media !== 'ready' && media !== 'nomic' && <p className="error">Media is not ready.</p>}
        {error && <p className="error">{error}</p>}
        {sorted.map((c) => {
          const on = feeding.includes(c.id);
          return (
            <div key={c.id} className="program-ch" data-sw={c.color}>
              <div>
                <div className="row">
                  <span className="dot" style={{ background: 'var(--sw)' }} />
                  <strong>{c.name}</strong>
                  <span className="chip chip--mono">{c.short}</span>
                  {c.kind === 'program' && <span className="chip chip--brand">PROGRAM</span>}
                  {c.kind === 'ifb' && <span className="chip chip--warning">IFB · DIM {c.dimDb} dB</span>}
                </div>
              </div>
              <button className={`btn btn--sm ${on ? 'btn--danger' : 'btn--primary'}`} disabled={busy === c.id || (media !== 'ready' && media !== 'nomic')} onClick={() => void toggle(c.id)}>
                {on ? <Square size={14} /> : <Radio size={14} />} {on ? 'Stop' : 'Feed'}
              </button>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}
